import { useQueryClient } from "@tanstack/react-query"
import { UserManager, WebStorageStateStore, type User } from "oidc-client-ts"
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react"
import { type NornClient, clientFor } from "./api"
import { type Connection, clearConnection, loadConnection, saveConnection } from "./connection"
import type { OidcConfig, ShellConfig } from "./shell-config"

/**
 * How this console is signed in. Development mode connects with a URL and token; a deployment with a
 * shell configuration signs in with the tenant IdP (authorization code + PKCE) and the cell verifies
 * the access token. `expired` means the session could not be renewed: the operator must sign in again.
 */
export type Auth =
  | { readonly kind: "dev" }
  | {
      readonly kind: "oidc"
      readonly status: "loading" | "signed-out" | "active" | "expired"
      /** Who the IdP says signed in (display only; the cell's `/v1/principal` is authoritative). */
      readonly profile: { readonly name: string | null; readonly email: string | null } | null
      signIn(returnTo: string): Promise<void>
      completeSignIn(): Promise<string>
      signOut(): Promise<void>
    }

interface SessionValue {
  readonly connection: Connection | null
  readonly client: NornClient | null
  readonly auth: Auth
  connect(connection: Connection): void
  disconnect(): void
}

const SessionContext = createContext<SessionValue | null>(null)

/** The tenant IdP client for this shell. Tokens live in sessionStorage: this tab only, gone when it closes. */
export function createUserManager(oidc: OidcConfig, origin: string): UserManager {
  return new UserManager({
    authority: oidc.issuer,
    client_id: oidc.clientId,
    redirect_uri: `${origin}/auth/callback`,
    post_logout_redirect_uri: `${origin}/`,
    response_type: "code",
    scope: oidc.scope,
    extraQueryParams: oidc.authorizeParams,
    // Renewal is on demand with the refresh token (an expired token at load, or a refused request);
    // never a hidden iframe, and no background timers.
    automaticSilentRenew: false,
    loadUserInfo: false,
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  })
}

/** Only same-origin, in-app paths are returned to after sign-in. */
export function safeReturnTo(value: unknown): string {
  return typeof value === "string" && value.startsWith("/") && !value.startsWith("//") ? value : "/"
}

/**
 * Whether this bundle may connect with a URL and a bearer token: development and the e2e build only.
 * A production bundle signs in with the tenant IdP or not at all; the token form is compiled out.
 */
export const DEV_CONNECT: boolean = import.meta.env.DEV || import.meta.env.MODE === "e2e"

/** Why this bundle cannot run with the configuration it was served, or null when it can. */
export function unusableConfig(config: ShellConfig | null, devConnect: boolean = DEV_CONNECT): string | null {
  if (config?.oidc || devConnect) return null
  return config
    ? "/norn-shell.json names no OIDC sign-in. A production console signs in with the tenant IdP only (see ENVIRONMENT.md)."
    : "/norn-shell.json is missing. A production console needs it to sign in with the tenant IdP (see ENVIRONMENT.md)."
}

export function SessionProvider({ children, initial, config = null }: { children: ReactNode; initial?: Connection | null; config?: ShellConfig | null }) {
  if (config?.oidc) {
    return (
      <OidcSession config={config} oidc={config.oidc}>
        {children}
      </OidcSession>
    )
  }
  const unusable = unusableConfig(config)
  if (unusable) throw new Error(unusable)
  return <DevSession initial={initial}>{children}</DevSession>
}

function DevSession({ children, initial }: { children: ReactNode; initial?: Connection | null }) {
  const [connection, setConnection] = useState<Connection | null>(() => (initial === undefined ? loadConnection() : initial))
  const queryClient = useQueryClient()
  const client = useMemo(() => (connection ? clientFor(connection) : null), [connection])
  // Cached reads belong to the identity that made them: a new connection (other
  // token or actor on the same cell) must never render the previous one's data.
  const connect = useCallback(
    (next: Connection) => {
      queryClient.clear()
      saveConnection(next)
      setConnection(next)
    },
    [queryClient],
  )
  const disconnect = useCallback(() => {
    queryClient.clear()
    clearConnection()
    setConnection(null)
  }, [queryClient])
  const value = useMemo(() => ({ connection, client, auth: { kind: "dev" } as const, connect, disconnect }), [connection, client, connect, disconnect])
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

/**
 * The live token and subject of one IdP session, mutated only by sign-in, renewals and sign-out.
 * `generation` changes on sign-out: a renewal begun before it must not bring the session back.
 * `renewal` is the one refresh in flight; a refresh token is redeemed once, however many requests
 * were refused meanwhile (IdPs rotate it and may revoke the whole family on reuse).
 */
class HeldSession {
  token: string | null = null
  subject: string | null = null
  generation = 0
  renewal: Promise<User | null> | null = null
}
const heldSessions = new WeakMap<UserManager, HeldSession>()
function heldFor(manager: UserManager): HeldSession {
  let held = heldSessions.get(manager)
  if (!held) {
    held = new HeldSession()
    heldSessions.set(manager, held)
  }
  return held
}

/** A fresh ID-token nonce per sign-in: oidc-client-ts checks the returned nonce only when one was sent. */
export function newNonce(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32))
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
}

/**
 * Renew with the refresh token, sharing one refresh across concurrent callers. Resolves null when the
 * session cannot be renewed, or when it was signed out meanwhile; a late renewal's tokens are removed
 * from storage instead of kept.
 */
async function renewOnce(manager: UserManager, generation: number): Promise<User | null> {
  const held = heldFor(manager)
  // Signed out (or in again) since the caller looked: its refusal must not start a refresh.
  if (held.generation !== generation) return null
  if (!held.renewal) {
    const renewal = manager
      .signinSilent()
      .catch(() => null)
      .then(async (user) => {
        if (heldFor(manager).generation === generation) return user
        if (user) await manager.removeUser().catch(() => undefined)
        return null
      })
      .finally(() => {
        if (held.renewal === renewal) held.renewal = null
      })
    held.renewal = renewal
  }
  return held.renewal
}

/** Callbacks already completed this page load: React may run an effect twice, a code is redeemed once. */
const completions = new Map<string, Promise<User>>()

function OidcSession({ children, config, oidc }: { children: ReactNode; config: ShellConfig; oidc: OidcConfig }) {
  const queryClient = useQueryClient()
  const manager = useMemo(() => createUserManager(oidc, window.location.origin), [oidc])
  const [user, setUser] = useState<User | null>(null)
  const [status, setStatus] = useState<"loading" | "signed-out" | "active" | "expired">("loading")
  // The current access token and subject live in HeldSession, read on every request so a renewal
  // applies without rebuilding the client; a different subject drops every cached read.
  const adopt = useCallback(
    (next: User) => {
      const held = heldFor(manager)
      if (held.subject !== null && held.subject !== next.profile.sub) queryClient.clear()
      held.subject = next.profile.sub
      held.token = next.access_token
      setUser(next)
      setStatus("active")
    },
    [queryClient, manager],
  )

  /**
   * Renew the session of `generation` (the one current when the caller started), then adopt the result
   * only if that session is still the current one: nothing begun before a sign-out outlives it.
   */
  const renew = useCallback(async (generation: number): Promise<User | null | "signed-out"> => {
    const renewed = await renewOnce(manager, generation)
    if (heldFor(manager).generation !== generation) return "signed-out"
    if (renewed) adopt(renewed)
    else setStatus("expired")
    return renewed
  }, [manager, adopt])

  const signOut = useCallback(async () => {
    const held = heldFor(manager)
    const inFlight = held.renewal
    held.generation += 1
    held.renewal = null
    held.token = null
    held.subject = null
    queryClient.clear()
    setUser(null)
    setStatus("signed-out")
    // A refresh already sent may still store tokens; let it settle (it removes them, being stale)
    // before this tab forgets the session, so nothing is left in storage to restore on return.
    await inFlight
    const metadata = await manager.metadataService.getMetadata().catch(() => null)
    // Ends the IdP session too when the IdP supports it; otherwise only this tab forgets the tokens.
    if (metadata?.end_session_endpoint) await manager.signoutRedirect()
    else await manager.removeUser()
  }, [manager, queryClient])

  useEffect(() => {
    let live = true
    void manager.getUser().then(async (stored) => {
      if (!live) return
      if (stored && !stored.expired) return adopt(stored)
      if (stored?.refresh_token) return void renew(heldFor(manager).generation)
      setStatus("signed-out")
    })
    // Every sign-in and renewal adopts its user explicitly (completeSignIn, renew); no library
    // events are needed, and none can re-enter React state.
    return () => {
      live = false
    }
  }, [manager, adopt, renew])

  // One retry after a refused token: renew with the refresh token, then send the request again.
  const authFetch = useCallback(
    async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      const base = new Request(input, init)
      const send = (bearer: string | null) => {
        const request = base.clone()
        if (bearer) request.headers.set("authorization", `Bearer ${bearer}`)
        return fetch(request)
      }
      const held = heldFor(manager)
      // The session this request belongs to; a refusal that arrives after sign-out renews nothing.
      const generation = held.generation
      const sent = held.token
      const response = await send(sent)
      if (response.status !== 401) return response
      if (held.generation !== generation || held.token === null) return response
      // Refused, but another request already renewed since this one was sent: use that token.
      if (held.token !== sent) return send(held.token)
      const renewed = await renew(generation)
      return renewed && renewed !== "signed-out" ? send(renewed.access_token) : response
    },
    [manager, renew],
  )

  const connection = useMemo<Connection | null>(() => (user && status !== "signed-out" ? { baseUrl: config.cell, token: user.access_token, actor: null } : null), [user, status, config.cell])
  // One client per cell: its fetch reads the current token, so renewals keep the query cache.
  const client = useMemo(() => clientFor({ baseUrl: config.cell, token: null, actor: null }, authFetch), [config.cell, authFetch])

  const auth = useMemo<Auth>(
    () => ({
      kind: "oidc",
      status,
      profile: user ? { name: typeof user.profile.name === "string" ? user.profile.name : null, email: typeof user.profile.email === "string" ? user.profile.email : null } : null,
      signIn: (returnTo) => manager.signinRedirect({ url_state: safeReturnTo(returnTo), nonce: newNonce() }),
      completeSignIn: async () => {
        const url = window.location.href
        let pending = completions.get(url)
        if (!pending) {
          pending = manager.signinRedirectCallback(url)
          completions.set(url, pending)
        }
        const signedIn = await pending
        adopt(signedIn)
        return safeReturnTo(signedIn.url_state)
      },
      signOut,
    }),
    [status, user, manager, adopt, signOut],
  )

  const value = useMemo<SessionValue>(
    () => ({
      connection,
      client: connection ? client : null,
      auth,
      // A configured deployment signs in with its IdP only; there is no token form to connect with.
      connect: () => undefined,
      disconnect: () => void signOut(),
    }),
    [connection, client, auth, signOut],
  )
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext)
  if (!value) throw new Error("useSession outside SessionProvider")
  return value
}

/** The connected client; routes under the scope layout are only reachable when connected. */
export function useCell(): { connection: Connection; client: NornClient } {
  const { connection, client } = useSession()
  if (!connection || !client) throw new Error("No cell connection")
  return { connection, client }
}
